import React from 'react';
import { defineMessages, useIntl } from 'react-intl';
import { Platform, StyleSheet, useWindowDimensions } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { useStyle } from 'react-native-style-utilities';

import {
  ContentfulImage,
  ContentfulImageTransformOptions,
  Image,
  theme,
  useResponsive,
} from '@sg/garnish';

import { BUNDLE_HERO_ASPECT_RATIO } from '../../Bundle.constants';

const BundleHeroFallbackImage = require('../../bundle-hero-fallback.webp');

// ─────────────────────────────────────────────────────────────────────────────

export const BundleHero = (props: Props) => {
  const { imageUrl, imageA11y } = props;

  const { formatMessage } = useIntl();
  const { currentBreakpoint } = useResponsive();
  const { width: windowWidth } = useWindowDimensions();
  const { top: safeAreaTopInset } = useSafeAreaInsets();

  // ─── Derived ─────────────────────────────────────────────────────────

  const isUnderStatusBar = Platform.OS === 'android' && currentBreakpoint.isXS;

  // ─── Styles ──────────────────────────────────────────────────────────

  const heroDynamicStyles = useStyle(() => {
    if (!isUnderStatusBar) {
      return;
    }

    return { height: windowWidth / BUNDLE_HERO_ASPECT_RATIO + safeAreaTopInset };
  }, [isUnderStatusBar, safeAreaTopInset, windowWidth]);

  const heroStyles = [styles.hero, heroDynamicStyles];

  // ─────────────────────────────────────────────────────────────────────

  if (!imageUrl) {
    return (
      <Image
        source={BundleHeroFallbackImage}
        alt={formatMessage(messages.fallbackImageA11y)}
        style={heroStyles}
      />
    );
  }

  return (
    <ContentfulImage
      alt={imageA11y ?? formatMessage(messages.fallbackImageA11y)}
      baseUrl={imageUrl}
      options={IMAGE_OPTIONS}
      style={heroStyles}
    />
  );
};

// ─── Constants ───────────────────────────────────────────────────────────────

const IMAGE_OPTIONS: Partial<ContentfulImageTransformOptions> = { fm: 'webp', w: 500 };

// ─── Styles ──────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  hero: {
    width: '100%',
    aspectRatio: BUNDLE_HERO_ASPECT_RATIO,
    backgroundColor: theme.colors.KALE,
  },
});

// ─── Messages ────────────────────────────────────────────────────────────────

const messages = defineMessages({
  fallbackImageA11y: {
    defaultMessage: 'An assortment of sweetgreen plates',
    description: 'Bundle | Hero | Fallback image accessibility label',
  },
});

// ─── Types ───────────────────────────────────────────────────────────────────

type Props = {
  imageUrl?: string;
  imageA11y?: string;
};
